import type { LucideIcon } from "lucide-react";
import AlertTriangleIcon from "lucide-react/dist/esm/icons/alert-triangle";
import InboxIcon from "lucide-react/dist/esm/icons/inbox";
import LockIcon from "lucide-react/dist/esm/icons/lock";
import RotateCcwIcon from "lucide-react/dist/esm/icons/rotate-ccw";
import type * as React from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * 業務データでは「空」「読み込み中」「エラー」「部分的失敗」「権限なし」が日常的に起きる。
 * 各画面がこれらを個別に書くと表現が割れるため、ここに集約する（原則 8）。
 */

interface StateShellProps {
  icon: LucideIcon;
  tone?: "neutral" | "critical" | "warning";
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode | undefined;
  className?: string | undefined;
}

function StateShell({
  icon: Icon,
  tone = "neutral",
  title,
  description,
  action,
  className
}: StateShellProps) {
  return (
    <div
      className={cn(
        "flex min-h-56 flex-col items-center justify-center gap-3 px-6 py-12 text-center",
        className
      )}
    >
      <span
        className={cn(
          "grid size-9 place-items-center rounded-lg border",
          tone === "critical" && "border-critical-line bg-critical-subtle text-critical-fg",
          tone === "warning" && "border-warning-line bg-warning-subtle text-warning-fg",
          tone === "neutral" && "border-line bg-surface-sunken text-fg-subtle"
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="grid gap-1">
        <p className="text-base font-semibold text-fg">{title}</p>
        <div className="mx-auto max-w-96 text-sm text-fg-muted">{description}</div>
      </div>
      {action ? <div className="mt-1 flex items-center gap-2">{action}</div> : null}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  /** 「データがありません」で終わらせない。次に何をすべきかを必ず書く。 */
  description: React.ReactNode;
  action?: React.ReactNode | undefined;
  className?: string | undefined;
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <StateShell
      icon={InboxIcon}
      title={title}
      description={description}
      action={action}
      className={className}
    />
  );
}

interface ErrorStateProps {
  title?: string | undefined;
  message: string;
  /** 問い合わせ時に使える識別子。分かる場合は必ず出す。 */
  requestId?: string | undefined;
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}

export function ErrorState({
  title = "データを読み込めませんでした",
  message,
  requestId,
  onRetry,
  className
}: ErrorStateProps) {
  return (
    <StateShell
      icon={AlertTriangleIcon}
      tone="critical"
      title={title}
      description={
        <>
          <span className="block">{message}</span>
          {requestId ? (
            <span className="mt-1 block font-mono text-xs text-fg-subtle">
              request id: {requestId}
            </span>
          ) : null}
        </>
      }
      action={
        onRetry ? (
          <Button size="sm" variant="outline" onClick={onRetry}>
            <RotateCcwIcon aria-hidden="true" />
            再試行
          </Button>
        ) : null
      }
      className={className}
    />
  );
}

interface ForbiddenStateProps {
  /** 何の権限が必要か */
  requiredRole: string;
  /** 誰に申請するか */
  contact?: string | undefined;
  className?: string | undefined;
}

export function ForbiddenState({ requiredRole, contact, className }: ForbiddenStateProps) {
  return (
    <StateShell
      icon={LockIcon}
      tone="warning"
      title="この画面を表示する権限がありません"
      description={
        <>
          <span className="block">
            表示には <strong className="font-semibold text-fg">{requiredRole}</strong>{" "}
            権限が必要です。
          </span>
          {contact ? <span className="mt-1 block">{contact} に申請してください。</span> : null}
        </>
      }
      className={className}
    />
  );
}

interface PartialFailureNoticeProps {
  /** 取得できた分は表示したうえで、失敗した範囲を明示する */
  message: string;
  onRetry?: (() => void) | undefined;
}

export function PartialFailureNotice({ message, onRetry }: PartialFailureNoticeProps) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-warning-line bg-warning-subtle px-4 py-2 text-xs text-warning-fg"
    >
      <AlertTriangleIcon className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry ? (
        <Button size="xs" variant="ghost" onClick={onRetry}>
          再取得
        </Button>
      ) : null}
    </div>
  );
}

interface TableSkeletonProps {
  rows?: number | undefined;
  columns?: number | undefined;
}

/** レイアウトシフトを起こさないよう、実際の行高（--row-h）で場所を確保する。 */
export function TableSkeleton({ rows = 8, columns = 5 }: TableSkeletonProps) {
  return (
    <div role="status" aria-label="読み込み中" className="divide-y divide-line">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: 静的なプレースホルダ行
          key={rowIndex}
          className="grid items-center gap-4 px-4"
          style={{
            height: "var(--row-h)",
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
          }}
        >
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: 静的なプレースホルダセル
              key={columnIndex}
              className="h-3"
              style={{ width: `${55 + ((rowIndex * 7 + columnIndex * 13) % 40)}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
